import { useState, type FormEvent } from "react";
import { useRecordMeasurement, useSelectFlange, type InspectionDetail } from "../../api/inspections";

const SECTION_TWO_LABELS: Record<string, string> = {
  "2.1": "Flange OD (mm)",
  "2.2": "Flange thickness (mm)",
  "2.3": "Number of holes",
  "2.4": "Hole diameter (mm)",
  "2.5": "Bolt size (thread only)",
  "2.6": "Valve PCD (mm)",
};

/** Chord and holes in, PCD out. The chord between hole centres and the count
 * of holes give the PCD; the server calculates it and matches it against the
 * flange tables. The preview here is the same formula, for the field
 * operators' comfort only: the stored number is the server's. */
export function previewPcd(chord: number, holes: number, gaps: number): number | null {
  if (!(chord > 0) || !(holes >= 3) || !(gaps >= 1)) {
    return null;
  }
  return chord / Math.sin((Math.PI * gaps) / holes);
}

function mm(value: number): string {
  return `${value.toFixed(2).replace(".", ",")} mm`;
}

/** Section 2: measure, calculate, match. Three measurements are needed; the
 * rest are taken if they can be. A tie or an out-of-tolerance result leaves
 * section 2 empty and lists the candidates; the office picks one with a
 * reason, which is what makes the record traceable. */
export default function MeasurementTab({ detail }: { detail: InspectionDetail }) {
  const id = detail.inspection.id;
  const valveId = detail.active_valve_id;
  const two = detail.section_two;
  const record = useRecordMeasurement(id);
  const choose = useSelectFlange(id);
  const canEdit = detail.actions.can_edit;
  const m = two.measurement;
  const [form, setForm] = useState({
    measured_chord_mm: m ? String(m.measured_chord_mm) : "",
    number_of_holes: m ? String(m.number_of_holes) : "",
    gaps_spanned: m ? String(m.gaps_spanned) : "1",
    valve_face_to_face_mm: m?.valve_face_to_face_mm?.toString() ?? "",
    pipe_od_mm: m?.pipe_od_mm?.toString() ?? "",
    pipe_flange_od_mm: m?.pipe_flange_od_mm?.toString() ?? "",
    pipe_flange_thickness_mm: m?.pipe_flange_thickness_mm?.toString() ?? "",
  });
  const [reason, setReason] = useState("");
  const [candidate, setCandidate] = useState("");

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const num = (v: string) => (v.trim() === "" ? null : Number(v));
  const preview = previewPcd(Number(form.measured_chord_mm), Number(form.number_of_holes), Number(form.gaps_spanned));
  const ready = preview !== null;

  function submit(event: FormEvent) {
    event.preventDefault();
    record.mutate({
      valve_id: valveId,
      measured_chord_mm: num(form.measured_chord_mm),
      number_of_holes: num(form.number_of_holes) === null ? null : Math.trunc(Number(form.number_of_holes)),
      gaps_spanned: Math.max(1, Math.trunc(Number(form.gaps_spanned) || 1)),
      standard: null,
      valve_face_to_face_mm: num(form.valve_face_to_face_mm),
      pipe_od_mm: num(form.pipe_od_mm),
      pipe_flange_od_mm: num(form.pipe_flange_od_mm),
      pipe_flange_thickness_mm: num(form.pipe_flange_thickness_mm),
    });
  }

  return (
    <div className="insp-section">
      <div className="insp-section-head">
        <h3>2 · Drilling (flange measurement)</h3>
        <span className="insp-section-sub">Measure the valve's flange; the PCD and the drilling are worked out from it.</span>
      </div>
      <form className="insp-fields" onSubmit={submit}>
        <label>
          Distance between two bolt-hole centres (mm) <span className="insp-needed">needed</span>
          <input className="num" inputMode="decimal" value={form.measured_chord_mm} onChange={(e) => set("measured_chord_mm", e.target.value)} readOnly={!canEdit} />
        </label>
        <label>
          Number of bolt holes <span className="insp-needed">needed</span>
          <input className="num" inputMode="numeric" value={form.number_of_holes} onChange={(e) => set("number_of_holes", e.target.value)} readOnly={!canEdit} />
        </label>
        <label>
          Holes apart
          <input className="num" inputMode="numeric" value={form.gaps_spanned} onChange={(e) => set("gaps_spanned", e.target.value)} readOnly={!canEdit} />
          <span className="field-hint">1 when you measured two holes next to each other.</span>
        </label>
        <p className="insp-hint">
          {preview === null ? "The PCD is worked out once the distance and the hole count are in." : `PCD works out to ${mm(preview)}.`}
          {detail.tolerance_mm ? ` A flange record within ±${detail.tolerance_mm} mm is a match.` : ""}
        </p>
        <details className="insp-more insp-fields-span" open={[form.valve_face_to_face_mm, form.pipe_od_mm, form.pipe_flange_od_mm, form.pipe_flange_thickness_mm].some((v) => v !== "")}>
          <summary>Also measure, if you can</summary>
          <div className="insp-fields">
            <label>
              Valve face to face (mm)
              <input className="num" inputMode="decimal" value={form.valve_face_to_face_mm} onChange={(e) => set("valve_face_to_face_mm", e.target.value)} readOnly={!canEdit} />
            </label>
            <label>
              Pipe OD (mm)
              <input className="num" inputMode="decimal" value={form.pipe_od_mm} onChange={(e) => set("pipe_od_mm", e.target.value)} readOnly={!canEdit} />
            </label>
            <label>
              Pipe flange OD (mm)
              <input className="num" inputMode="decimal" value={form.pipe_flange_od_mm} onChange={(e) => set("pipe_flange_od_mm", e.target.value)} readOnly={!canEdit} />
            </label>
            <label>
              Pipe flange thickness (mm)
              <input className="num" inputMode="decimal" value={form.pipe_flange_thickness_mm} onChange={(e) => set("pipe_flange_thickness_mm", e.target.value)} readOnly={!canEdit} />
            </label>
          </div>
        </details>
        {canEdit ? (
          <button type="submit" className="btn btn-primary" disabled={record.isPending || !ready}>
            Calculate and match
          </button>
        ) : null}
      </form>
      {record.isError ? <p className="notice bad">{record.error.message}</p> : null}

      {m ? (
        <section className="insp-subsection">
          <h4>Calculated</h4>
          <dl className="insp-facts">
            <dt>PCD</dt>
            <dd>{mm(m.calculated_pcd)}</dd>
            <dt>From</dt>
            <dd>
              {m.measured_chord_mm} mm between centres, {m.number_of_holes} holes, {m.gaps_spanned} apart
            </dd>
          </dl>
        </section>
      ) : null}

      {two.flange_match ? (
        <section className="insp-subsection matched">
          <h4>Matched: {two.flange_match.label}</h4>
          <p className="insp-hint">
            {two.flange_match.nominal_bore} · PCD {two.flange_match.matched_pcd} ({two.flange_match.delta_mm >= 0 ? "+" : ""}
            {two.flange_match.delta_mm.toFixed(3)} mm)
            {two.flange_match.override_reason ? ` · chosen by the office: ${two.flange_match.override_reason}` : ""}
          </p>
          <dl className="insp-facts">
            {Object.entries(two.populated).map(([key, value]) => (
              <div key={key} className="fact-pair">
                <dt>
                  {key} {SECTION_TWO_LABELS[key] ?? ""}
                </dt>
                <dd>{value === null ? "—" : String(value)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {two.unresolved ? (
        <section className="insp-subsection unresolved">
          <h4>Section 2 stays empty</h4>
          <p className="notice warn">{two.unresolved.message}</p>
          {two.candidates.length > 0 ? (
            detail.actions.can_override && canEdit ? (
              <form
                className="insp-row-fields"
                onSubmit={(e) => {
                  e.preventDefault();
                  choose.mutate({ valve_id: valveId, flange_reference_id: Number(candidate), reason: reason.trim() || null });
                }}
              >
                <label>
                  Record
                  <select value={candidate} onChange={(e) => setCandidate(e.target.value)} required>
                    <option value="">Choose…</option>
                    {two.candidates.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Reason
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this record" />
                </label>
                <button type="submit" className="btn btn-primary" disabled={choose.isPending || candidate === ""}>
                  Use this record
                </button>
              </form>
            ) : (
              <ul className="insp-plain-list">
                {two.candidates.map((c) => (
                  <li key={c.id}>{c.label}</li>
                ))}
                <li className="insp-hint">An office user chooses between these.</li>
              </ul>
            )
          ) : null}
          {choose.isError ? <p className="notice bad">{choose.error.message}</p> : null}
        </section>
      ) : null}
    </div>
  );
}
